import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import Layout from './shared/layout';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './services/common/auth-context';
import { createRTLCache } from './shared/rtl-cache';
import { useTranslation } from 'react-i18next';
import { register } from './serviceWorkerRegistration';
import './shared/i18n';
import { LanguageCode } from './helper-types/general/consts/language-code';

// Extend the Window interface to include service worker types
declare global {
  interface ServiceWorkerRegistration {
    // Add any specific service worker registration properties if needed
  }
}

const Root: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [direction, setDirection] = React.useState<'ltr' | 'rtl'>(
    (localStorage.getItem('direction') as 'ltr' | 'rtl') || 'ltr'
  );

  React.useEffect(() => {
    localStorage.setItem('direction', direction);
    document.body.dir = direction;
    i18n.changeLanguage(direction === 'rtl' ? LanguageCode.AR : LanguageCode.EN).then(() => {
      document.title = i18n.t('home.EyadtiSystem');
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  const cache = React.useMemo(() => createRTLCache(direction === 'rtl'), [direction]);

  return (
    <CacheProvider value={cache}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Layout setDirection={setDirection} />
        </BrowserRouter>
      </AuthProvider>
    </CacheProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

// Register service worker for PWA
if (import.meta.env.PROD) {
  register({
    onSuccess: (registration: ServiceWorkerRegistration) => {
      console.log('ServiceWorker registration successful with scope: ', registration.scope);
    },
    onUpdate: (registration: ServiceWorkerRegistration) => {
      console.log('New content is available; please refresh.');
      // You could prompt the user to update here if desired
    },
  });
}